import { useMutation, useQueryClient } from "@tanstack/react-query";

import { IAddPatientDeviceRequest } from "../../../@types/admin/AdminDevice";
import { patientDevicesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Every device list is stale after a write — all patients' and each one's. */
function useInvalidateDevices() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ["admin-patient-devices"] });
}

/** Registers a device to a patient. */
export function useAddPatientDevice() {
  const invalidate = useInvalidateDevices();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    IAddPatientDeviceRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await patientDevicesService.addDevice(request)),
    onSuccess: invalidate,
  });

  return { addDevice: mutateAsync, isAddingDevice: isPending };
}

/**
 * ⚠️ Disconnects a device from its patient — their readings stop flowing.
 * Retool guarded this with a confirm dialog and so does the panel.
 */
export function useDisconnectPatientDevice() {
  const invalidate = useInvalidateDevices();

  const { mutateAsync, isPending } = useMutation<unknown, Error, string>({
    mutationFn: async (identifier) =>
      ensureOk(await patientDevicesService.disconnectDevice(identifier)),
    onSuccess: invalidate,
  });

  return { disconnectDevice: mutateAsync, isDisconnecting: isPending };
}

interface IUpdateIdentifierArgs {
  identifier: string;
  newIdentifier: string;
}

/** Renames a device's identifier. */
export function useUpdateDeviceIdentifier() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateDevices();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    IUpdateIdentifierArgs
  >({
    mutationFn: async ({ identifier, newIdentifier }) =>
      ensureOk(
        await patientDevicesService.updateIdentifier(identifier, newIdentifier)
      ),
    onSuccess: () => {
      invalidate();
      // History is keyed by identifier, so the old key now points at nothing.
      queryClient.invalidateQueries({ queryKey: ["admin-device-history"] });
    },
  });

  return { updateIdentifier: mutateAsync, isUpdatingIdentifier: isPending };
}
